import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";

const CancelledConfirmation = ({ t }) => (
  <>
    <CheckCircle2 size={48} className="text-gold mx-auto mb-4" />
    <h1 className="font-serif text-2xl text-white mb-2">{t("cancelPage.cancelledTitle")}</h1>
    <p className="text-white/60 mb-6">{t("cancelPage.cancelledMessage")}</p>
    <Link to="/boeken">
      <Button className="bg-gold text-white hover:bg-gold-hover rounded-none px-8">{t("booking.success.newBooking")}</Button>
    </Link>
  </>
);

const CancelForm = ({ t, lang, booking, token, error, submitting, onCancel }) => (
  <>
    <h1 className="font-serif text-2xl text-white mb-4">{t("cancelPage.title")}</h1>
    <div className="text-left bg-[#1A1A1A] rounded-lg p-4 mb-6 space-y-1.5 text-sm">
      {booking.appointment_number && (
        <p className="flex justify-between text-white/70"><span>{t("manageAppointmentPage.appointmentNumber")}</span><span className="text-gold font-semibold">#{booking.appointment_number}</span></p>
      )}
      <p className="flex justify-between text-white/70"><span>{t("booking.summary.service")}</span><span className="text-white">{lang === "nl" ? booking.service_name_nl : booking.service_name_en}</span></p>
      <p className="flex justify-between text-white/70"><span>{t("booking.summary.date")}</span><span className="text-white">{booking.date}</span></p>
      <p className="flex justify-between text-white/70"><span>{t("booking.summary.time")}</span><span className="text-white">{booking.start_time}</span></p>
    </div>
    <p className="text-white/50 text-sm mb-3">{t("cancelPage.confirmText")}</p>
    <p data-testid="cancel-policy-note" className="text-gold/70 text-xs bg-gold/5 border border-gold/20 rounded-md p-3 mb-6 leading-relaxed">{t("cancelPage.policyNote")}</p>
    {error && <p data-testid="cancel-error-message" className="text-red-400 text-sm mb-4">{error}</p>}
    <Button data-testid="cancel-confirm-button" onClick={onCancel} disabled={submitting} className="w-full bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20 rounded-none disabled:opacity-60">
      {submitting ? t("common.loading") : t("cancelPage.confirmButton")}
    </Button>
    <Link to={`/afspraak-wijzigen?token=${token}`} data-testid="cancel-page-manage-link" className="block text-white/50 text-xs mt-4 hover:text-gold underline underline-offset-4">
      {t("cancelPage.manageLink")}
    </Link>
  </>
);

export default function CancelAppointment() {
  const { t, lang } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [cancelled, setCancelled] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t("cancelPage.invalidLink"));
      return;
    }
    api.get(`/bookings/by-token/${token}`).then(({ data }) => {
      setBooking(data);
      if (data.status === "cancelled") setCancelled(true);
    }).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, [token, t]);

  const handleCancel = async () => {
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/bookings/cancel/${token}`);
      setCancelled(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="cancel-appointment-page" className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full bg-[#141414] border border-white/10 rounded-xl p-8 text-center">
        {error && !booking && (
          <>
            <XCircle size={48} className="text-red-400 mx-auto mb-4" />
            <p data-testid="cancel-error-message" className="text-white/70">{error}</p>
          </>
        )}
        {booking && cancelled && <CancelledConfirmation t={t} />}
        {booking && !cancelled && (
          <CancelForm t={t} lang={lang} booking={booking} token={token} error={error} submitting={submitting} onCancel={handleCancel} />
        )}
      </div>
    </div>
  );
}
